import { PackageOpen, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface PackageOffering {
  id: string;
  name: string;
  description: string | null;
  serviceId: string;
  quantity: number;
  priceMinor: number;
  currency: string;
  validityDays: number;
  isActive: number;
  position: number;
}

interface Service {
  id: string;
  name: string;
  category: string;
}

function formatMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);
}

async function apiFetch(path: string, opts?: RequestInit): Promise<Response> {
  const res = await fetch(path, { credentials: 'include', ...opts });
  return res;
}

export function PackagesSettings() {
  const [offerings, setOfferings] = useState<PackageOffering[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    name: '',
    serviceId: '',
    quantity: 5,
    priceMajor: '100.00',
    validityDays: 365,
  });
  const [submitting, setSubmitting] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [pkgRes, svcRes] = await Promise.all([
        apiFetch('/api/packages/offerings').then((r) => r.json()),
        apiFetch('/api/services').then((r) => r.json()),
      ]);
      setOfferings((pkgRes as { offerings?: PackageOffering[] }).offerings ?? []);
      const svcData = svcRes as { services?: Service[] } | Service[];
      setServices(Array.isArray(svcData) ? svcData : (svcData.services ?? []));
    } catch (e) {
      setError((e as Error).message ?? 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function create() {
    const priceMinor = Math.round(Number.parseFloat(draft.priceMajor) * 100);
    if (!draft.name.trim() || !draft.serviceId || priceMinor <= 0) {
      setError('Fill out all required fields.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch('/api/packages/offerings', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: draft.name.trim(),
          serviceId: draft.serviceId,
          quantity: draft.quantity,
          priceMinor,
          validityDays: draft.validityDays,
        }),
      });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error ?? 'Create failed');
        return;
      }
      setDraft({ name: '', serviceId: '', quantity: 5, priceMajor: '100.00', validityDays: 365 });
      await refresh();
    } catch (e) {
      setError((e as Error).message ?? 'Create failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function deactivate(id: string) {
    if (!confirm('Deactivate this package? Customers can no longer purchase it.')) return;
    setError(null);
    try {
      const res = await apiFetch(`/api/packages/offerings/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error ?? 'Deactivate failed');
        return;
      }
      await refresh();
    } catch (e) {
      setError((e as Error).message ?? 'Deactivate failed');
    }
  }

  async function deletePermanently(id: string, name: string) {
    if (!confirm(`Delete "${name}" permanently? This cannot be undone.`)) return;
    setError(null);
    try {
      const res = await apiFetch(`/api/packages/offerings/${id}/permanent`, { method: 'DELETE' });
      if (!res.ok) {
        const data = (await res.json()) as { error?: string };
        setError(data.error ?? 'Delete failed');
        return;
      }
      await refresh();
    } catch (e) {
      setError((e as Error).message ?? 'Delete failed');
    }
  }

  if (loading) return <div className="py-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-8 py-6">
      <header className="flex items-center gap-2">
        <PackageOpen className="h-6 w-6" />
        <h1 className="text-2xl font-semibold">Multi-stay packages</h1>
      </header>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <section className="space-y-3 rounded-lg border bg-card p-6">
        <h2 className="text-lg font-semibold">New offering</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Name *</span>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="rounded-md border bg-background px-2 py-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Service *</span>
            <select
              value={draft.serviceId}
              onChange={(e) => setDraft({ ...draft, serviceId: e.target.value })}
              className="rounded-md border bg-background px-2 py-1"
            >
              <option value="">Select…</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.category})
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Stays in pack</span>
            <input
              type="number"
              value={draft.quantity}
              onChange={(e) =>
                setDraft({ ...draft, quantity: Number.parseInt(e.target.value, 10) })
              }
              min={1}
              className="rounded-md border bg-background px-2 py-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Price (major units)</span>
            <input
              value={draft.priceMajor}
              onChange={(e) => setDraft({ ...draft, priceMajor: e.target.value })}
              className="rounded-md border bg-background px-2 py-1"
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-muted-foreground">Validity days</span>
            <input
              type="number"
              value={draft.validityDays}
              onChange={(e) =>
                setDraft({ ...draft, validityDays: Number.parseInt(e.target.value, 10) })
              }
              min={1}
              className="rounded-md border bg-background px-2 py-1"
            />
          </label>
        </div>
        <button
          type="button"
          onClick={() => void create()}
          disabled={submitting}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {submitting ? 'Creating…' : 'Create'}
        </button>
      </section>

      <section className="rounded-lg border bg-card">
        {offerings.length === 0 ? (
          <div className="px-4 py-8 text-center text-muted-foreground text-sm">
            No package offerings yet. Create one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[560px] w-full text-sm">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Stays</th>
                  <th className="px-4 py-2">Price</th>
                  <th className="px-4 py-2">Validity</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {offerings.map((o) => (
                  <tr key={o.id} className="border-b last:border-0">
                    <td className="px-4 py-2 font-medium">{o.name}</td>
                    <td className="px-4 py-2">{o.quantity}</td>
                    <td className="px-4 py-2">{formatMoney(o.priceMinor, o.currency)}</td>
                    <td className="px-4 py-2">{o.validityDays}d</td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          o.isActive
                            ? 'inline-flex rounded-full bg-success/10 px-2 py-0.5 text-xs font-medium text-success'
                            : 'inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground'
                        }
                      >
                        {o.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-2">
                        {o.isActive === 1 ? (
                          <button
                            type="button"
                            onClick={() => void deactivate(o.id)}
                            className="text-xs text-muted-foreground hover:text-destructive"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void deletePermanently(o.id, o.name)}
                            className="inline-flex items-center gap-1 text-xs text-destructive hover:underline"
                            title="Delete permanently"
                          >
                            <Trash2 className="size-3" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
